import {
  CallDataSource,
  CallDto,
  FilterOptionDto,
} from '@act/shared/data-transfer-objects';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { Fragment, useContext, useEffect, useState } from 'react';
import Loader from 'react-components/workflows/loader';
import { useForm } from 'react-hook-form';
import {
  callsAppSelectors,
  getCallFilters,
  getCalls,
} from 'redux/slices/callsSlice';
import { useAppDispatch } from 'redux/store-hooks';
import styled from 'styled-components';
import { themeColors } from 'toolkit/styles/colors';
import FilterDialog from '../../member-index/filter-dialog';
import CallRow from './call-row';
import CallsActionMenu from './calls-action-menu';
import CallFilters from './calls-filters';
import SortableCallsTableHeaders, {
  Order,
  getComparator,
  stableSort,
} from './calls-table-headers';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import PostCallSummaryDrawer from 'react-components/member-page/post-call-summary-drawer/PostCallSummaryDrawer';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import PostCallSummaryContext from 'react-components/member-page/post-call-summary-drawer/PostCallSummaryContext';
import { useDisplayModeContext } from '@act/storybook';
import type { BaseFilterConfig, PaginatedFilterConfig } from './types';
import type {
  StyledTableContainer,
  StyledTablePagination,
} from '../../../types/mui-styled-components';

const IndexParent = styled.div`
  padding: 0;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const IndexContainer = styled(TableContainer)<{
  component?: React.ElementType;
  elevation?: number;
}>`
  position: relative;
  overflow-x: initial;
  overflow-y: none;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
` as StyledTableContainer;

const Pagination = styled(TablePagination)<{ component?: React.ElementType }>`
  position: sticky;
  bottom: -16px;
  z-index: 2;
  background-color: ${themeColors.containerLight};
` as StyledTablePagination;

export type CallFiltersForm = {
  [key: string]: FilterOptionDto[];
} & {
  onlyUnassignedCalls: boolean;
  onlyFeedbackCalls: boolean;
  showOnlyMyCalls: boolean;
};

const CallsIndex = ({ active }: { active: boolean }) => {
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const { displayMode } = useDisplayModeContext();
  const [bounds, setBounds] = useState<{ size: number; start: number }>({
    size: 10,
    start: 0,
  });

  const dispatch = useAppDispatch();

  const callDataQualityFF = useFeatureFlag('call-data-quality');
  const { voiceCallId, setVoiceCallId } = useContext(PostCallSummaryContext);

  const callFilterLoadingStatus =
    callsAppSelectors.useSelectCallsFiltersLoadingStatus();
  const filters = callsAppSelectors.useSelectCallsFilters()?.filters;

  useEffect(() => {
    async function getFilters() {
      void dispatch(getCallFilters({}));
    }

    if (!filters && callFilterLoadingStatus !== 'loading') {
      getFilters();
    }
  }, [filters, callFilterLoadingStatus]);

  const [open, setOpen] = useState<boolean>(false);
  const [filterConfig, setFilterConfig] = useState<BaseFilterConfig>(null);
  const [loadedFilterConfig, setLoadedFilterConfig] =
    useState<BaseFilterConfig>(null);

  function getConfigFromStore() {
    const existingConfig = window.localStorage.getItem('callsFilterConfig');
    if (existingConfig) {
      const parsedConfig = JSON.parse(existingConfig);
      if (parsedConfig.lastDaysFromNow === undefined) {
        parsedConfig.lastDaysFromNow = 30;
      }
      return parsedConfig;
    }

    return null;
  }

  function getDefaults(reset?: boolean): {
    [key: string]: unknown[] | boolean | number | string;
  } {
    const existingConfig = getConfigFromStore();

    if (existingConfig && !reset) {
      return existingConfig;
    }

    const defaults = {
      searchTerm: '',
      includeCount: true,
      searchTermType: null,
      'page-start': 0,
      'page-size': 10,
      subjectIds: null,
      orderBy: null,
      directionType: null,
      orderDirection: null,
      onlyFeedbackCalls: false,
      onlyUnassignedCalls: false,
      lastDaysFromNow: 30,
      betweenDates: null,
      statuses: [],
      guideIds: [],
    };
    filters?.forEach(f => (defaults[f.key] = []));
    return defaults;
  }

  useEffect(() => {
    if (!filterConfig || !active) {
      return;
    }

    // Prevent duplicate calls to the API.
    if (loadedFilterConfig === filterConfig) {
      return;
    }

    setLoadedFilterConfig(filterConfig);
    void dispatch(
      getCalls({
        params: {
          ...filterConfig,
          ...(filterConfig.guideIds && {
            guideIds: filterConfig.guideIds as string[],
          }),
          ...(filterConfig.statuses && {
            statuses: filterConfig.statuses as string[],
          }),
        },
      })
    );
  }, [filterConfig]);

  const useFormState = useForm<PaginatedFilterConfig>({
    defaultValues: getDefaults(),
  });

  const { control, reset, getValues, setValue } = useFormState;

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof CallDataSource>(null);
  const [additionalMenuAnchor, setAdditionalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [currentCall, setCurrentCall] = useState<CallDto>(null);
  const openMenu = Boolean(additionalMenuAnchor);

  const calls = callsAppSelectors.useSelectCallsPage();
  const totalRecords = callsAppSelectors.useSelectCallsPageTotal();
  const isLoading: boolean =
    callsAppSelectors.useSelectCallsAreLoading() === 'loading';

  useEffect(() => {
    function setDefaults() {
      const parsedFilterConfig = getConfigFromStore();

      const defaults = getDefaults();

      const reconciledDefaults = {
        ...defaults,
        ...parsedFilterConfig,
      };

      reset(reconciledDefaults);
    }

    if (filters?.length) {
      setDefaults();
    }

    setValue('page-start', 0);
    void handleFiltering();
  }, []);

  async function handleReset() {
    reset(getDefaults(true));
    window.localStorage.removeItem('callsFilterConfig');
    // Reset pagination bounds to first page when resetting filters
    setBounds(prevBounds => ({ ...prevBounds, start: 0 }));
    await handleFiltering();
  }

  async function handleFiltering(resetPage?: boolean): Promise<void> {
    const filterValues = getValues();
    const filterConfig = { 'page-start': 0 };

    if (resetPage) {
      filterValues['page-start'] = 0;
      // Reset pagination bounds to first page
      setBounds(prevBounds => ({ ...prevBounds, start: 0 }));
    }

    Object.entries(getValues())
      .filter(
        ([k, v]) =>
          ![
            'guideId',
            'onlyFeedbackCalls',
            'onlyUnassignedCalls',
            'showOnlyMyCalls',
            'page-start',
            'page-size',
            'orderBy',
            'directionType',
            'searchTermType',
            'searchTerm',
            'subjectIds',
          ].includes(k) && (v as FilterOptionDto[])?.length
      )
      .forEach(([key, val]) => {
        filterConfig[key] = val as FilterOptionDto[];
      });

    filterConfig['onlyFeedbackCalls'] = filterValues['onlyFeedbackCalls'];
    filterConfig['onlyUnassignedCalls'] = filterValues['onlyUnassignedCalls'];
    filterConfig['showOnlyMyCalls'] = filterValues['showOnlyMyCalls'];
    filterConfig['page-start'] = filterValues['page-start'];
    filterConfig['page-size'] = filterValues['page-size'];
    filterConfig['includeCount'] = true;
    filterConfig['orderBy'] = filterValues['orderBy'];
    filterConfig['directionType'] = filterValues['directionType'];
    filterConfig['searchTermType'] = filterValues['searchTermType'];
    filterConfig['lastDaysFromNow'] = filterValues['lastDaysFromNow'];
    filterConfig['betweenDates'] = filterValues['betweenDates'];
    filterConfig['subjectIds'] = filterValues['subjectIds']
      ? [filterValues['subjectIds']]
      : [];
    filterConfig['searchTerm'] = filterValues['searchTerm'];

    window.localStorage.setItem(
      'callsFilterConfig',
      JSON.stringify({ ...filterConfig })
    );

    setFilterConfig({
      ...filterConfig,
    } as PaginatedFilterConfig);

    setOpen(false);
  }

  const openAdditionalMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    call: CallDto
  ) => {
    setCurrentCall(call);
    setAdditionalMenuAnchor(event.currentTarget);
  };

  function closeAdditionalMenu() {
    setCurrentCall(null);
    setAdditionalMenuAnchor(null);
  }

  async function handleSortChange(orderBy, orderDirection) {
    const filterValues = getValues();
    const newValues = {
      ...filterValues,
      'page-start': 0,
      orderBy,
      orderDirection,
    };
    reset(newValues);

    void dispatch(
      getCalls({
        params: {
          ...filterConfig,
          orderBy: orderBy === 'timeStarted' ? 'createdAt' : orderBy,
          orderDirection,
          'page-size': bounds.size,
          'page-start': bounds.start,
        },
      })
    );
  }

  function handleRequestSort(
    _event: React.MouseEvent<unknown>,
    property: keyof CallDataSource
  ) {
    const isAsc = orderBy === property && order === 'asc';
    const sortOrder = isAsc ? 'desc' : 'asc';
    void handleSortChange(property, sortOrder);
    setOrder(sortOrder);
    setOrderBy(property);
  }

  async function handlePageChange(newPageNumber: number) {
    setBounds(previousBounds => {
      return { ...previousBounds, start: newPageNumber };
    });
    // CALCULATING PAGE START; SHOULD DISCUSS MOVING TO BACKEND
    void dispatch(
      getCalls({
        params: {
          ...filterConfig,
          'page-size': bounds.size,
          'page-start': bounds.size * newPageNumber,
        },
      })
    );
  }

  function countActiveFilters() {
    const count = Object.entries(getValues())
      .filter(
        ([k]) =>
          ![
            'searchTerm',
            'searchTermType',
            'subjectIds',
            'orderBy',
            'orderDirection',
            'onlyUnassignedCalls',
            'page-start',
            'page-size',
            'includeCount',
          ].includes(k)
      )
      .reduce((acc, [_key, val]) => {
        if (!(val as FilterOptionDto[])?.length) return acc;
        return ++acc;
      }, 0);
    return count;
  }

  async function handleRowsPerPageChange(newPageSize: number) {
    setBounds(previousBounds => {
      return { ...previousBounds, size: newPageSize };
    });
    void dispatch(
      getCalls({
        params: {
          ...filterConfig,
          'page-size': newPageSize,
          'page-start': bounds.start,
        },
      })
    );
  }
  return (
    <IndexParent>
      <IndexContainer component={Paper} elevation={0}>
        <CallFilters
          control={control}
          handleOpenFilters={() => setOpen(true)}
          handleFiltering={() => handleFiltering(true)}
          activeFilterCount={countActiveFilters()}
          setValue={setValue}
          getValues={getValues}
          currentFilterConfig={filterConfig}
          disabled={isLoading}
        />
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {isLoading ? (
            <Loader />
          ) : (
            <Table stickyHeader aria-label="calls table">
              <colgroup>
                <col width="18%" />
                <col width="18%" />
                <col width="18%" />
                <col width="18%" />
                <col width="11.5%" />
                <col width="11.5%" />
                <col width="5%" />
              </colgroup>
              <SortableCallsTableHeaders
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
              />
              <TableBody>
                {!!calls?.length && (
                  <Fragment>
                    {stableSort(
                      calls as unknown as CallDataSource[],
                      getComparator(order, orderBy)
                    ).map(call => (
                      <CallRow
                        key={call?.id}
                        actingUser={actingUser}
                        call={call}
                        openAdditionalMenu={openAdditionalMenu}
                      />
                    ))}
                  </Fragment>
                )}
              </TableBody>
              <CallsActionMenu
                anchorEl={additionalMenuAnchor}
                open={openMenu}
                onClose={closeAdditionalMenu}
                call={currentCall}
                actingUser={actingUser}
              />
            </Table>
          )}
        </Box>
        <Pagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords || 0}
          rowsPerPage={bounds.size || 10}
          page={bounds.start || 0}
          onPageChange={(_event, page) => {
            void handlePageChange(page);
          }}
          onRowsPerPageChange={event => {
            void handleRowsPerPageChange(parseInt(event.target.value));
          }}
          disabled={isLoading}
        />
      </IndexContainer>
      <FilterDialog
        open={open}
        control={control}
        filters={filters}
        handleReset={handleReset}
        handleClose={() => setOpen(false)}
        handleFiltering={() => handleFiltering(true)}
      />
      {callDataQualityFF && (
        <PostCallSummaryDrawer
          open={voiceCallId !== null}
          displayMode={displayMode}
          onClose={() => setVoiceCallId(null)}
        />
      )}
    </IndexParent>
  );
};

export default CallsIndex;
